import { store } from '@redux/store';
import { fromEvent } from 'rxjs';
import { scheduleTour, setModalStep } from '../redux/slices';
import ModalStep from '../enums/modalStep';
import UserStatus from '../enums/userStatus';
import VerificationMethod from '../enums/verificationMethod';
import { getVerificationMethodStep } from '../services/verificationMethodService';

const VERIFICATION_METHODS_LABELS = {
  [VerificationMethod.Identification]: 'government-issued photo identification card',
  [VerificationMethod.CreditCard]: 'credit / debit card',
};

export default class TermsAndConditions {
  forSaleCheckboxSubscription$ = null;

  tacCheckboxSubscription$ = null;

  tacTriggerSubscription$ = null;

  tacPrivacyTriggerSubscription$ = null;

  previousStep = ModalStep.TourConfiguration;

  constructor(rootComponent) {
    this.rootComponent = rootComponent;
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate);
  }

  getFormattedVerificationMethods() {
    return this.rootComponent.verificationMethods
      .filter(method => method.name !== 'Phone')
      .map(method => VERIFICATION_METHODS_LABELS[method.name])
      .sort((a, b) => b.localeCompare(a))
      .join(' or a ');
  }

  static getForSaleCheckbox() {
    return document.querySelector('[data-self-guided-tour-tac-sale-only-check]');
  }

  static getTacCheckbox() {
    return document.querySelector('[data-self-guided-tour-tac-terms-check]');
  }

  static getTacModalTrigger() {
    return document.querySelector('[data-self-guided-tour-tac-modal-trigger]');
  }

  static getForSaleOnlyCheckbox() {
    return `<p class="nhs-c-input__container--with-text nhs-u-items-flex-start">
            <input class="nhs-c-input__check-box" id="for-sale" data-self-guided-tour-tac-sale-only-check type="checkbox" ${
              this.isForSaleChecked ? 'checked' : ''
            } />
            <label for="for-sale">
                *I understand this is a home FOR SALE and not a home FOR RENT.
            </label>
          </p>`;
  }

  primaryModalButtonAction = () => {
    const tosCheckbox = TermsAndConditions.getTacCheckbox();
    tosCheckbox.checked = true;
    this.onMainCheckboxChange();
    this.rootComponent.innerModal.hide();
  };

  /**
   * Handles the action for the secondary button by hiding the inner modal
   * of the root component.
   *
   * @function
   */
  secondaryButtonAction = () => {
    this.rootComponent.innerModal.hide();
  };

  /**
   * Resets the default values for the terms and conditions and sale status.
   */
  setDefaultValues = () => {
    this.isTermsAndCondtionChecked = false;
    this.isForSaleChecked = false;
    this.skipQuestions = false;
  };

  onDomUpdate = (event) => {
    if (event.detail?.step === ModalStep.TermsAndConditions) {
      const { isForRent } = this.rootComponent.getSpecDetails();
      const disablePrimaryButton = !this.isTermsAndCondtionChecked || (!isForRent && !this.isForSaleChecked);
      this.rootComponent.setPrimaryButtonDisabled(disablePrimaryButton);
      this.rootComponent.setSecondaryButtonDisabled(false);
      if (Object.values(this.rootComponent.tourQuestions).length === 0) {
        this.rootComponent.footer.setPrimaryButtonText('Accept and schedule');
        this.skipQuestions = true;
      }
      this.setupEventHandlers();
      this.setModalOnLinks();
    } else {
      this.setDefaultValues();
      this.teardownEventHandlers();
      this.rootComponent.innerModal.reset();
    }
  };

  setModalOnLinks() {
    const { termsAndConditionsSrc } = this.rootComponent.getSpecDetails();
    const modalContent = `
      <h1 class="nhs-c-heading--self-guided-tour nhs-u-padding--x-840">Visitor access terms & conditions</h1>
      <div class="nhs-c-dialog__scrollable-content--35vh" data-self-guided-tour-tac-modal-content>
        <iframe src="${termsAndConditionsSrc}" width="100%" height="100%" frameborder="0"></iframe>
      </div>`;
    this.rootComponent.innerModal.updateContent(modalContent);
    this.rootComponent.innerModal.updateButtons({
      primaryButtonText: 'I accept',
      primaryButtonAction: this.primaryModalButtonAction,
      secondaryButtonText: 'Cancel',
      secondaryButtonAction: this.secondaryButtonAction,
    });
  }

  setupEventHandlers() {
    const forSaleCheckbox = TermsAndConditions.getForSaleCheckbox();
    if (forSaleCheckbox) {
      this.forSaleCheckboxSubscription$ = fromEvent(forSaleCheckbox, 'change').subscribe(
        this.onMainCheckboxChange.bind(this)
      );
    }

    this.tacCheckboxSubscription$ = fromEvent(TermsAndConditions.getTacCheckbox(), 'change').subscribe(
      this.onMainCheckboxChange.bind(this)
    );

    this.tacTriggerSubscription$ = fromEvent(TermsAndConditions.getTacModalTrigger(), 'click').subscribe((event) => {
      event.preventDefault();
      this.rootComponent.innerModal.show();
      this.rootComponent.disableScroll();
    });
  }

  teardownEventHandlers() {
    this.forSaleCheckboxSubscription$?.unsubscribe();
    this.tacCheckboxSubscription$?.unsubscribe();
    this.tacTriggerSubscription$?.unsubscribe();
    this.tacPrivacyTriggerSubscription$?.unsubscribe();
  }

  /**
   * Handles the change event for the main checkbox (terms and conditions).
   *
   */
  onMainCheckboxChange() {
    const forSaleCheckbox = TermsAndConditions.getForSaleCheckbox();
    const termsAndConditionsCheckbox = TermsAndConditions.getTacCheckbox();
    this.isTermsAndCondtionChecked = termsAndConditionsCheckbox.checked;
    this.isForSaleChecked = forSaleCheckbox?.checked;

    let requiredChecked = this.isTermsAndCondtionChecked;
    if (forSaleCheckbox) {
      requiredChecked = requiredChecked && this.isForSaleChecked;
    }
    this.rootComponent.setPrimaryButtonDisabled(!requiredChecked);
  }

  /**
   * Handles the transition to the next panel in the modal based on the user's status.
   * Depending on the user's verification status, it dispatches an action to update
   * the modal step in the application state.
   */
  moveToNextPanel() {
    switch (UserStatus.fromKey(this.rootComponent.userStatus?.name)) {
      case UserStatus.Verified:
        if (this.skipQuestions) {
          // Disable the primary button to prevent multiple clicks
          this.rootComponent.setPrimaryButtonDisabled(true);
          store.dispatch(scheduleTour());
        } else {
          store.dispatch(setModalStep({ modalStep: ModalStep.Questions }));
        }
        break;
      case UserStatus.NotVerified: {
        const step = getVerificationMethodStep(this.rootComponent.verificationMethods);
        store.dispatch(setModalStep({ modalStep: step }));
        break;
      }
      case UserStatus.NotFound:
      default:
        store.dispatch(setModalStep({ modalStep: ModalStep.SignUp }));
        break;
    }
  }

  /**
   * Handles the primary action for the tour configuration.
   * Dispatches an action to update the modal step in the store with the next step.
   *
   */
  handlePrimaryAction() {
    this.moveToNextPanel();
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application's state.
   *
   */
  handleSecondaryAction() {
    if (Object.keys(this.rootComponent.otherHomes).length > 0) {
      store.dispatch(setModalStep({ modalStep: this.previousStep }));
      return;
    }
    store.dispatch(setModalStep({ modalStep: ModalStep.DateTimeSelection }));
  }

  render() {
    const { isForRent, termsAndConditionsSrc, privacyPolicyHref } = this.rootComponent.getSpecDetails();

    return `
      <div class="nhs-c-self-guided-left-panel">
        <div>   
          <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Terms &amp; conditions</h1>

          <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

          <p class="nhs-c-label--black-900-body-1-medium-regular">
            In order to register and schedule your private tour, review the terms & conditions below.</p>
        </div>
        <div class="nhs-o-flex-column-items-start nhs-u-gap--600">
          ${isForRent ? '' : TermsAndConditions.getForSaleOnlyCheckbox()}
          <p class="nhs-c-input__container--with-text nhs-u-items-flex-start">
            <input class="nhs-c-input__check-box" data-self-guided-tour-tac-terms-check id="terms-conditions" type="checkbox" ${
              this.isTermsAndCondtionChecked ? 'checked' : ''
            } />
            <label for="terms-conditions">
                *I agree to the <a class="nhs-c-standard-link--large-underlined" href="${termsAndConditionsSrc}" data-self-guided-tour-tac-modal-trigger>Visitor Access Terms and Conditions</a> and understand that my information will be used as described in the <a class="nhs-c-standard-link--large-underlined" target="_blank" href="${privacyPolicyHref}">Privacy Policy</a>. I understand that UTour will require an identity verification process that will require the use of my ${this.getFormattedVerificationMethods()}. I understand that UTour will disclose the information I provide to one or more third-party vendors for the purpose of comparison and analysis for the sole purpose of verifying my identity. (Required).
            </label>
          </p>
        </div>
      </div>
    `;
  }
}
